/**
 * Inline SVG icons.
 *
 * Inlined rather than pulled from an icon package: it keeps the bundle small,
 * removes a dependency, and means the icons render instantly with no flash on
 * a cold load — which matters when the app is opened mid-crisis.
 */

const base = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.75,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  viewBox: '0 0 24 24',
};

export const HomeIcon = (): JSX.Element => (
  <svg {...base} aria-hidden="true">
    <path d="M3 10.5 12 3l9 7.5" />
    <path d="M5 9.5V20a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V9.5" />
  </svg>
);

export const PenIcon = (): JSX.Element => (
  <svg {...base} aria-hidden="true">
    <path d="M4 20h4l10-10a2.5 2.5 0 0 0-3.5-3.5L4.5 16.5 4 20Z" />
    <path d="M13.5 7.5 16.5 10.5" />
  </svg>
);

export const PrinterIcon = (): JSX.Element => (
  <svg {...base} aria-hidden="true">
    <path d="M7 9V3h10v6" />
    <path d="M7 19H5a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
    <path d="M7 15h10v6H7z" />
  </svg>
);

export const ShareIcon = (): JSX.Element => (
  <svg {...base} aria-hidden="true">
    <circle cx="18" cy="5" r="2.5" />
    <circle cx="6" cy="12" r="2.5" />
    <circle cx="18" cy="19" r="2.5" />
    <path d="M8.2 10.8 15.8 6.4M8.2 13.2l7.6 4.4" />
  </svg>
);

export const HeartIcon = (): JSX.Element => (
  <svg {...base} aria-hidden="true">
    <path d="M12 20s-7-4.5-7-9.5A3.5 3.5 0 0 1 12 8a3.5 3.5 0 0 1 7 2.5C19 15.5 12 20 12 20Z" />
  </svg>
);

export const BrainIcon = (): JSX.Element => (
  <svg {...base} aria-hidden="true">
    <path d="M9 4a2.5 2.5 0 0 0-2.5 2.5A2.5 2.5 0 0 0 4 9v2a2.5 2.5 0 0 0 1.5 2.3A2.5 2.5 0 0 0 7 18h2" />
    <path d="M15 4a2.5 2.5 0 0 1 2.5 2.5A2.5 2.5 0 0 1 20 9v2a2.5 2.5 0 0 1-1.5 2.3A2.5 2.5 0 0 1 17 18h-2" />
    <path d="M12 4v16" />
  </svg>
);

/** Days: a month grid. Copied into help.html's tab card; keep both the same. */
export const CalendarIcon = (): JSX.Element => (
  <svg {...base} aria-hidden="true">
    <rect x="3" y="5" width="18" height="16" rx="2" />
    <path d="M3 10h18" />
    <path d="M8 3v4M16 3v4" />
    <path d="M8 14h.01M12 14h.01M16 14h.01M8 18h.01M12 18h.01" />
  </svg>
);

export const ChartIcon = (): JSX.Element => (
  <svg {...base} aria-hidden="true">
    <path d="M4 20V10" />
    <path d="M10 20V4" />
    <path d="M16 20v-7" />
    <path d="M22 20H2" />
  </svg>
);

export const LifebuoyIcon = (): JSX.Element => (
  <svg {...base} aria-hidden="true">
    <circle cx="12" cy="12" r="9" />
    <circle cx="12" cy="12" r="3.5" />
    <path d="m5.6 5.6 3.9 3.9M14.5 14.5l3.9 3.9M18.4 5.6l-3.9 3.9M9.5 14.5l-3.9 3.9" />
  </svg>
);

export const CheckIcon = (): JSX.Element => (
  <svg {...base} aria-hidden="true">
    <path d="m4 12.5 5 5L20 6.5" />
  </svg>
);

export const ArrowLeftIcon = (): JSX.Element => (
  <svg {...base} aria-hidden="true">
    <path d="M19 12H5" />
    <path d="m11 18-6-6 6-6" />
  </svg>
);

export const MicIcon = (): JSX.Element => (
  <svg {...base} aria-hidden="true">
    <rect x="9" y="3" width="6" height="11" rx="3" />
    <path d="M5 11a7 7 0 0 0 14 0" />
    <path d="M12 18v3" />
  </svg>
);

export const BookIcon = (): JSX.Element => (
  <svg {...base} aria-hidden="true">
    <path d="M4 5.5A1.5 1.5 0 0 1 5.5 4H10a2 2 0 0 1 2 2v14a1.5 1.5 0 0 0-1.5-1.5h-5A1.5 1.5 0 0 1 4 17V5.5Z" />
    <path d="M20 5.5A1.5 1.5 0 0 0 18.5 4H14a2 2 0 0 0-2 2v14a1.5 1.5 0 0 1 1.5-1.5h5A1.5 1.5 0 0 0 20 17V5.5Z" />
  </svg>
);

/**
 * A flag, not an alarm.
 *
 * Drawn as a circle rather than the usual warning triangle: this marks the one
 * line on Today saying doses count as taken until you say otherwise, and a
 * triangle reads as something having gone wrong. Nothing has. It is a note that
 * wants looking at once a day.
 */
export const AlertIcon = (): JSX.Element => (
  <svg {...base} aria-hidden="true">
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7.5v5.5" />
    <path d="M12 16.25v.5" />
  </svg>
);

/** For a dose that has not come round yet — waiting, rather than done. */
export const ClockIcon = (): JSX.Element => (
  <svg {...base} aria-hidden="true">
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5.2l3.2 1.9" />
  </svg>
);

export const SparkIcon = (): JSX.Element => (
  <svg {...base} aria-hidden="true">
    <path d="M12 3v4M12 17v4M3 12h4M17 12h4" />
    <path d="m6 6 2.5 2.5M15.5 15.5 18 18M18 6l-2.5 2.5M8.5 15.5 6 18" />
  </svg>
);

/**
 * Two speech bubbles, one leaning toward the other.
 *
 * For the "want to talk this through?" offer, which had no mark at all and read
 * as one more saved box on a page full of them. A bubble says conversation
 * without saying alarm — which rules out the obvious alternatives: a warning
 * triangle would tell someone they have been flagged, a heart would be the app
 * being sentimental at a person having a bad night, and a lifebuoy already means
 * the crisis screen and must go on meaning only that.
 *
 * The lean is the whole idea. Two upright bubbles are a messaging app; one
 * turned slightly toward the other is someone listening.
 */
export const TalkBubblesIcon = (): JSX.Element => (
  <svg {...base} aria-hidden="true">
    <path d="M3.5 7.5A2.5 2.5 0 0 1 6 5h7a2.5 2.5 0 0 1 2.5 2.5v3A2.5 2.5 0 0 1 13 13H8.5L5 15.5V13a1.5 1.5 0 0 1-1.5-1.5Z" />
    <path d="M17 9.5h1.5A2.5 2.5 0 0 1 21 12v3a2.5 2.5 0 0 1-2.5 2.5H18v2.2L15 17.5h-2" opacity="0.55" />
  </svg>
);

export const TrashIcon = (): JSX.Element => (
  <svg {...base} aria-hidden="true">
    <path d="M4 7h16" />
    <path d="M10 4h4a1 1 0 0 1 1 1v2H9V5a1 1 0 0 1 1-1z" />
    <path d="M6 7v13a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V7" />
    <path d="M10 11v6M14 11v6" />
  </svg>
);

/**
 * Therapy homework: a clipboard with a couple of lines on it.
 *
 * A clipboard rather than a bookmark or a star, because what this marks is a
 * task somebody else set — the reading should be "an assignment", not "a
 * favourite". Same stroke weight and 24px box as the rest, so it sits in the
 * Today rows beside the heart and the microphone without drawing more
 * attention than they do.
 */
export const ClipboardIcon = (): JSX.Element => (
  <svg {...base} aria-hidden="true">
    <path d="M9 4h6a1 1 0 0 1 1 1v1H8V5a1 1 0 0 1 1-1Z" />
    <path d="M16 6h2a1 1 0 0 1 1 1v13a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h2" />
    <path d="M9 12h6" />
    <path d="M9 16h4" />
  </svg>
);
